import loadable from '@loadable/component';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteProps, Route } from 'react-router-dom';
import {
  useAsyncEffect,
  useLinkTo,
  useMount,
} from '../../../hooks';
import nonCriticalException from '../../../modules/exceptionLogger';
import { RouteGuardConfig } from '../../../routes';
import store from '../../../store';
import type { IRootDispatch, RootState } from '../../../store/models';
import storeReady from '../../../store/ready';
import SubsBlockerSelectors from '../../../store/SubsBlocker.selectors';
import { requireLogin } from '../login/SignUpLoginModal';
import { AuthOrigins } from '../../../modules/user/constants';
import * as Page from '../../../modules/PageMeta';
import { HOME_ROUTE } from '../../../route-names';

type GuardableRouteProps = RouteProps & RouteGuardConfig;

const RestorationBlockerContainer = loadable(
  () => import('../../provider/restoration/RestorationBlockerContainer'),
  { resolveComponent: components => components.RestorationBlockerContainer },
);

export const GuardableRoute: React.FCWithChildren<GuardableRouteProps> = ({
  loginFailedRedirect,
  loginRequired,
  loginOrigin,
  loginMessage,
  loggedOutTitle,
  subsRequired,
  ...routeProps
}) => {
  const redirectTo = useLinkTo({ replace: true });
  const [authorized, setAuthorized] = useState<boolean>(false);
  const dispatch = useDispatch<IRootDispatch>();
  const userId = useSelector<RootState, number | null>(state => state.user.userId);
  const showSubsBlocker = useSelector<RootState, boolean>(
    state => subsRequired && SubsBlockerSelectors.shouldShowSubsBlocker(state),
  );

  useEffect(() => {
    if (loggedOutTitle !== undefined) {
      Page.setMeta({ title: loggedOutTitle });
    }
  }, [loggedOutTitle]);

  useMount(async () => {
    await storeReady;

    try {
      // if we're a direct land, we may not have loaded user information yet
      await dispatch.user.whoami({});
    } catch (e) {
      nonCriticalException(e);
    }

    if (loginRequired) {
      const loggedInUserId = store.getState().user?.userId;

      if (!loggedInUserId) {
        dispatch.loader.setIsLoading(false);
        requireLogin({
          mode: 'clientlogin',
          origin: loginOrigin as AuthOrigins,
          helperText: loginMessage,
        })
          .then(({ userData }) => {
            if (!userData.user_id) {
              throw new Error(`${routeProps.path}: Successful login but user_id is undefined`);
            }
            setAuthorized(true);
          })
          .catch(e => {
            nonCriticalException(
              `${routeProps.path}: Anon user cancelled login`,
              { ...(e?.msg && { message: e.msg }) },
            );
            redirectTo(loginFailedRedirect || HOME_ROUTE);
          });
      } else {
        // login is required, but there's already a user ID
        setAuthorized(true);
      }
    } else {
      // always wait for store ready in guarded routes, regardless of user requirements
      setAuthorized(true);
    }
  });

  useAsyncEffect(async () => {
    if (!subsRequired) return;
    await store.dispatch.subsBlocker.update();
  }, [
    userId,
    subsRequired,
  ]);

  const canRenderContents = authorized && !showSubsBlocker;

  return (
    <React.Fragment>
      {showSubsBlocker && <RestorationBlockerContainer />}
      {canRenderContents && <Route {...routeProps} />}
    </React.Fragment>
  );
};
